import { useGetCurrentUserQuery, useGetDemoQuery } from '@carboncopyco/data';
import {
  ArchiveIcon,
  BalloonOutlineIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  LinkIcon,
  OpenInNew1OutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { Dropdown, DropdownItem, Separator } from '@carboncopyco/ui';
import { useDemoAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactNode, useState } from 'react';
import { useCopyUrl, useIsDemoLocked } from '../../../hooks';
import {
  ArchiveDemoModal,
  CopyDemoModalWithFolders,
  DuplicateDemoModal,
  MoveDemoModal,
} from '../../AssetActions/Demo';
import { AssetLockedModal } from '../../AssetLockedModal';
import { useDesktopDeeplink, usePermissionsContext } from '../../utils';
import { useOverlayPageProps } from './OverlayPageContext';

type OverlayPageActionsDropdownProps = {
  triggerElement: ReactNode;
};

export const OverlayPageDropdown = ({
  triggerElement,
}: OverlayPageActionsDropdownProps) => {
  const {
    handleCreateTour,
    goToEditor,
    demoId,
    organizationId,
    goToDuplicatedDemo,
    goToCopiedDemo,
    workspaceId,
    goToWorkspacePage,
    goToFolderPage,
    goToDemoPreview,
  } = useOverlayPageProps();
  const {
    trackDemoClickEdit,
    trackDemoClickDuplicate,
    trackDemoClickCreateTour,
    trackDemoClickArchive,
    trackDemoClickCopyToWorkspace,
    trackDemoMultiUserLock,
  } = useDemoAnalytics('demo_live-app_actions-dropdown');
  const { data: demo } = useGetDemoQuery({
    id: demoId,
    workspaceId,
    organizationId,
  });
  const { getPermissions } = usePermissionsContext();
  const flags = useFlags();
  const { copyCurrentURL } = useCopyUrl();

  const deleteDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.deleteDemoById
  );
  const editDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );
  const duplicateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.duplicateDemo
  );
  const createTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.createTour
  );
  const copyToWorkspacePermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.copyDemo
  );
  const moveDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.moveDemo
  );

  const [
    showDuplicateDemoModal,
    {
      setTrue: setTrueShowDuplicateDemoModal,
      setFalse: setFalseShowDuplicateDemoModal,
    },
  ] = useBoolean(false);
  const [
    showArchiveDemoModal,
    {
      setTrue: setTrueShowArchiveDemoModal,
      setFalse: setFalseShowArchiveDemoModal,
    },
  ] = useBoolean(false);
  const { exeDeeplinkWithAction, shouldShowDeeplink } = useDesktopDeeplink();

  const [isDemoLockedModal, setIsDemoLockedModal] = useState(false);
  const { data: userData } = useGetCurrentUserQuery();

  const { checkIsDemoLocked, userName } = useIsDemoLocked({
    demoId,
    organizationId,
    currentUserEmail: userData.user.email,
    shouldRunInterval: false,
  });

  const overlayFolderId = String(demo?.folderId);

  const handleEditDemo = () => {
    trackDemoClickEdit(demo);
    exeDeeplinkWithAction(
      {
        action: 'edit',
        assetId: demoId,
        organizationId,
        workspaceId,
        assetType: 'live_demo',
      },
      goToEditor
    );
  };

  const { trackDemoClickPreview } = useDemoAnalytics(
    'overlay_info_page_top_actions'
  );

  return demo ? (
    <>
      <Dropdown
        triggerElement={triggerElement}
        width={200}
        align="center"
        asChild
        arrow
      >
        <div
          className={classNames('p-2 flex flex-col', {
            'gap-2': editDemoPermission && deleteDemoPermission,
          })}
        >
          <DropdownItem
            iconRight={
              shouldShowDeeplink ? <OpenInNew1OutlineIcon size={15} /> : null
            }
            iconLeft={<ShowOutlineIcon />}
            label="Preview"
            onClick={() => {
              trackDemoClickPreview();
              goToDemoPreview();
            }}
          />
          {editDemoPermission && (
            <DropdownItem
              iconRight={
                shouldShowDeeplink ? <OpenInNew1OutlineIcon size={15} /> : null
              }
              iconLeft={<EditIcon size={18} />}
              label="Edit"
              onClick={async () => {
                if (flags.editorMultiUserLocking) {
                  const lockedBy = await checkIsDemoLocked();
                  if (lockedBy) {
                    setIsDemoLockedModal(true);

                    trackDemoMultiUserLock({
                      variables: {
                        userEditing: lockedBy,
                        userTryingToEdit: userData.user.email,
                      },
                      demoId,
                    });

                    return;
                  }
                }

                handleEditDemo();
              }}
            />
          )}
          {createTourPermission && (
            <DropdownItem
              iconLeft={<BalloonOutlineIcon size={18} />}
              label="Create tour"
              onClick={() => {
                demo && trackDemoClickCreateTour(demo);
                handleCreateTour();
              }}
            />
          )}
          {duplicateDemoPermission && (
            <DropdownItem
              iconLeft={<DuplicateOutlineIcon size={18} />}
              label="Duplicate"
              onClick={() => {
                trackDemoClickDuplicate(demo);
                setTrueShowDuplicateDemoModal();
              }}
            />
          )}
          {moveDemoPermission && (
            <DropdownItem
              iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
              label="Move to..."
              onClick={() => {
                NiceModal.show(MoveDemoModal, {
                  organizationId,
                  demo,
                  workspaceId,
                });
              }}
            />
          )}
          {copyToWorkspacePermission && (
            <DropdownItem
              iconLeft={<CopyOutlineIcon size={18} />}
              label="Copy to..."
              onClick={() => {
                trackDemoClickCopyToWorkspace(demo);
                NiceModal.show(CopyDemoModalWithFolders, {
                  organizationId,
                  demo,
                  goToCopiedDemo,
                });
              }}
            />
          )}
          {flags.copyUrl && (
            <DropdownItem
              iconLeft={<LinkIcon size={18} />}
              label="Copy page URL"
              onClick={() => {
                copyCurrentURL();
              }}
            />
          )}
          {deleteDemoPermission && (
            <>
              <Separator horizontal />
              <DropdownItem
                iconLeft={<ArchiveIcon size={18} />}
                label="Archive"
                onClick={() => {
                  trackDemoClickArchive(demo);
                  setTrueShowArchiveDemoModal();
                }}
              />
            </>
          )}
        </div>
      </Dropdown>
      {showDuplicateDemoModal && (
        <DuplicateDemoModal
          onClose={setFalseShowDuplicateDemoModal}
          organizationId={organizationId}
          demoId={demoId}
          show={showDuplicateDemoModal}
          goToDuplicatedDemo={goToDuplicatedDemo}
          folderId={String(demo.folderId)}
        />
      )}
      {showArchiveDemoModal && (
        <ArchiveDemoModal
          folderId={overlayFolderId}
          onClose={setFalseShowArchiveDemoModal}
          demoId={demoId}
          show={showArchiveDemoModal}
          organizationId={organizationId}
          navigateOnSuccess={() =>
            overlayFolderId === '0'
              ? goToWorkspacePage()
              : goToFolderPage(overlayFolderId, demo?.workspaceId)
          }
        />
      )}
      {isDemoLockedModal && (
        <AssetLockedModal
          userName={userName}
          buttonLabel="Okay"
          onButtonClick={() => {
            setIsDemoLockedModal(false);
          }}
        />
      )}
    </>
  ) : null;
};
